/**
 * Review network for the demo.
 *
 * The planted finding here is review-load concentration: Priya and Marcus carry
 * most of the review burden while everyone else reviews around the edges. That
 * is the shape the Review Network page exists to reveal, and it maps directly
 * onto the EM/Team Lead pain in our ICP ("who reviews everything, and what
 * happens when they are on holiday").
 *
 * Load is derived from each developer's `reviewGiveWeight` / `reviewReceiveWeight`
 * in the fixture rather than hand-listed per pair, so the picture stays
 * consistent with the contributor lists on every other page.
 */

import type { ReviewNetworkResponse, ReviewPair, TopReviewer } from '@/types/insights';
import { DEMO_ORG_ID, resolveContributors } from '../fixture';
import { demoDate, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoReviewParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoReviewParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

export function buildDemoReviewNetwork(params: DemoReviewParams): ReviewNetworkResponse {
  const people = resolveContributors(params.repoId, params.teamId, params.excludeBots);
  const rng = rngFor(seedInput(params, 'review/network'), 'pairs');

  // Scale with the window, damped: reviews accumulate but a year is not 52
  // weeks of identical activity.
  const windowScale = (params.days / 30) ** 0.86;

  const reviewers = [...people].sort((a, b) => b.reviewGiveWeight - a.reviewGiveWeight);
  const pairs: ReviewPair[] = [];

  for (const reviewer of reviewers) {
    if (reviewer.reviewGiveWeight <= 0) continue;
    // A reviewer's reach tracks their load: hubs review nearly everyone, the
    // long tail reviews one or two people. A flat fan-out would render as an
    // even mesh and hide the concentration the page is meant to surface.
    const reach = Math.max(1, Math.round((reviewer.reviewGiveWeight / 32) * (people.length - 1)));
    const authors = people
      .filter((p) => p.id !== reviewer.id)
      .sort((a, b) => b.reviewReceiveWeight - a.reviewReceiveWeight)
      .slice(0, reach);

    for (const author of authors) {
      const count = Math.max(
        1,
        Math.round(
          (reviewer.reviewGiveWeight * author.reviewReceiveWeight) / 22 * windowScale * jitter(rng, 0.18)
        )
      );
      pairs.push({
        reviewer_id: reviewer.id,
        reviewer_login: reviewer.login,
        reviewer_name: reviewer.name,
        author_id: author.id,
        author_login: author.login,
        author_name: author.name,
        review_count: count,
      });
    }
  }

  const totalsByReviewer = new Map<string, number>();
  for (const pair of pairs) {
    totalsByReviewer.set(pair.reviewer_id, (totalsByReviewer.get(pair.reviewer_id) ?? 0) + pair.review_count);
  }

  const top_reviewers: TopReviewer[] = people
    .map((dev) => ({
      developer_id: dev.id,
      external_login: dev.login,
      name: dev.name,
      total_reviews: totalsByReviewer.get(dev.id) ?? 0,
    }))
    .filter((r) => r.total_reviews > 0)
    .sort((a, b) => b.total_reviews - a.total_reviews);

  return {
    organization_id: DEMO_ORG_ID,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    review_pairs: pairs.sort((a, b) => b.review_count - a.review_count),
    top_reviewers,
    // Derived from the pairs rather than drawn separately, so the headline
    // total always equals what the graph shows.
    total_reviews: pairs.reduce((sum, p) => sum + p.review_count, 0),
  };
}
